<template>
  <q-dialog
    v-model="showModal"
    @hide="onHide"
    transition-show="slide-up"
    transition-hide="slide-down"
  >
    <q-card class="modal-detail-campaign" style="height: 90vh; max-height: 90vh;">
      <q-linear-progress :value="1" class="bg-primary" />
      <q-toolbar class="bg-primary text-white">
        <q-toolbar-title class="text-h6">
          {{ $gettext('VIEW_CAMPAIGN') }}
        </q-toolbar-title>
        <q-btn v-close-popup flat round dense icon="close" />
      </q-toolbar>

      <q-card-section class="detail-content q-pa-md">
        <div class="row q-col-gutter-md">
          <!-- LEFT PANEL -->
          <div class="col-12 col-md-6 detail-col-left">

            <!-- Campaign Information -->
            <q-card flat class="q-mb-lg">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                  <div class="crud-title justify-between row items-center">
                    <span>{{ $gettext('CAMPAIGN_INFORMATION') }}</span>
                    <q-chip :icon="getCampaignStatusIcon()"
                      :class="`status-chip status-chip--${getCampaignStatusKey()}`"
                      dense>
                      {{ getCampaignStatusLabel() }}
                    </q-chip>
                  </div>
              </q-card-section>
              <q-card-section>
                <div class="info-row q-mb-md">
                  <div class="info-label">{{ $gettext('CAMPAIGN_NAME') }}</div>
                  <div class="info-value text-weight-bold">{{ campaignData.name }}</div>
                </div>

                <div class="row q-col-gutter-md q-mb-md">
                  <div class="col-6">
                    <div class="info-row">
                      <div class="info-label">{{ $gettext('START_DATE') }}</div>
                      <div class="row items-center q-gutter-sm">
                        <q-icon name="event" size="16px" style="color: #00613b;" />
                        <span class="info-value">{{ formatAuditDate(campaignData.startDate) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="info-row">
                      <div class="info-label">{{ $gettext('END_DATE') }}</div>
                      <div class="row items-center q-gutter-sm">
                        <q-icon name="event" size="16px" style="color: #d18e4b;" />
                        <span class="info-value">{{ campaignData.endDate ? formatAuditDate(campaignData.endDate) : $gettext('NO_END_DATE') }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row q-col-gutter-md q-mb-md">
                  <div class="col-6">
                    <div class="info-row">
                      <div class="info-label">{{ $gettext('RECURRENCE') }}</div>
                      <div class="row items-center q-gutter-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #0e88e4;" aria-hidden="true">
                          <path d="m17 2 4 4-4 4"></path>
                          <path d="M3 11v-1a4 4 0 0 1 4-4h14"></path>
                          <path d="m7 22-4-4 4-4"></path>
                          <path d="M21 13v1a4 4 0 0 1-4 4H3"></path>
                        </svg>
                        <span class="info-value">{{ getRecurrenceLabel() }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="campaignData.leadTimeDays" class="col-6">
                    <div class="info-row">
                      <div class="info-label">{{ $gettext('LEAD_TIME_DAYS_HINT') }}</div>
                      <div class="row items-center q-gutter-sm">
                        <q-icon name="access_time" size="16px" style="color: #0e88e4;" />
                        <span class="info-value text-caption">{{ campaignData.leadTimeDays }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="shouldShowCustomInterval" class="row q-col-gutter-md q-mb-md">
                  <div class="col-12">
                    <div class="info-row">
                      <div class="info-label">{{ $gettext('CUSTOM_INTERVAL') }}</div>
                      <div class="info-value">{{ campaignData.customInterval }}</div>
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <q-card v-if="campaignData.template" flat class="card-template q-mb-lg">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title">
                  <span>{{ $gettext('SEGMENTATION_FILTER') }}</span>
                </div>
              </q-card-section>
              <q-card-section>
                <div class="col-12" v-if="campaignData.segmentationFilter">
                  <!-- Top-level label, if present -->
                  <div v-if="campaignData.segmentationFilter.label" class="info-row q-mb-sm">
                    <div class="info-label">{{ campaignData.segmentationFilter.label }}</div>
                  </div>

                  <!-- Loop through fields in segmentationFilter -->
                  <div v-for="(val, key) in campaignData.segmentationFilter" :key="key">
                    <div v-if="key !== 'label' && val != null" class="info-row q-mb-md">
                      <div class="info-label">{{ $gettext(toSnakeUpper(key)) }}</div>
                      <div class="info-value">{{ getSegmentationFieldLabel(val) }}</div>
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <!-- Linked Template -->
            <q-card v-if="campaignData.template" flat class="card-template q-mb-lg">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title">
                  <span>{{ $gettext('TEMPLATE_LINKED') }}</span>
                </div>
              </q-card-section>
              <q-card-section>
                <div class="template-card">
                  <div class="template-card-icon">
                    <div class="purpose-icon-wrapper">
                      <q-icon :name="getPurposeIcon(campaignData.template.messagingPurpose)" size="24px" color="#0e88e4" />
                    </div>
                  </div>
                  <div class="template-card-content">
                    <p class="template-card-name">{{ campaignData.template.name }}</p>
                    <p class="template-card-meta">
                        <q-icon :name="getChannelIcon(campaignData.template.channelType)" size="16px" :color="getChannelColor(campaignData.template.channelType)" />
                        {{ campaignData.template.channelType_nmDescricao }}
                    </p>
                  </div>
                  <div class="template-card-status">
                    <span class="status-dot" :class="campaignData.template.available ? 'status-dot--active' : 'status-dot--inactive'"></span>
                    <span class="text-caption text-weight-bold">
                      {{ campaignData.template.available ? $gettext('ACTIVE') : $gettext('INACTIVE') }}
                    </span>
                  </div>
                </div>
              </q-card-section>
            </q-card>

            <!-- Executions -->
            <campaign-executions-list
              :campaign-executions="campaignData.campaignExecutions"
            />
          </div>

          <!-- RIGHT PANEL: Message Preview -->
          <div class="col-12 col-md-6 detail-col-right">
            <campaign-preview-panel
              :subject="(campaignData.template && campaignData.template.subject) || ''"
              :body-html="(campaignData.template && campaignData.template.content) || ''"
              :purpose-label="(campaignData.template && campaignData.template.messagingPurpose_nmDescricao) || ''"
              :channel-type="campaignData.template && campaignData.template.channelType"
              :header-image="headerImagePreview"
              :footer-image="footerImagePreview"
              :message-variables="messageVariables"
              :start-date="campaignData.startDate"
              :end-date="campaignData.endDate"
              :lead-time-days="campaignData.leadTimeDays"
            />
          </div>
        </div>
      </q-card-section>

      <!-- Footer -->
      <q-card-section class="detail-footer q-pa-md row justify-center q-gutter-x-md">
        <q-btn
          :label="$gettext('CLOSE')"
          color="primary"
          @click="onClose"
          class="q-px-xl"
        />
        <q-btn
          :label="$gettext('EDIT')"
          color="green"
          @click="onEdit"
          class="q-px-xl"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { messageBox, repository } from 'jtech'
import { formatDateTimeLocal } from 'src/shared/utils/dateUtils.js'
import CampaignPreviewPanel from './CampaignPreviewPanel.vue'
import CampaignExecutionsList from './CampaignExecutionsList.vue'
import { campaignStatus } from 'src/shared/inf-tabela-descricao/CampaignStatus.enum.js'
import { parseSegmentationFilter, formatSegmentationFilterDisplay, formatSegmentationFilterTitle } from '../utils/segmentationFilterUtils.js'
import { messagingPurpose } from 'src/shared/inf-tabela-descricao/MessagingPurpose.enum.js'
import { recurrenceType } from 'src/shared/inf-tabela-descricao/RecurrenceType.enum.js'
import { templateImageType } from 'src/shared/inf-tabela-descricao/TemplateImageType.enum.js'
import { getChannelColor, getChannelIcon } from '../utils/channelMappings.js'

@Component({
  name: 'CampaignDetailModal',
  components: {
    CampaignPreviewPanel,
    CampaignExecutionsList
  }
})
class CampaignDetailModal extends Vue {
  @Prop(Boolean) show
  @Prop(Object) channelTypes

  get HEADER_IMAGE_TYPE () {
    return templateImageType().HEADER.key
  }

  get FOOTER_IMAGE_TYPE () {
    return templateImageType().FOOTER.key
  }

  get shouldShowCustomInterval () {
    return this.campaignData.customInterval && String(this.campaignData.recurrenceType) === String(recurrenceType().CUSTOM.key)
  }

  getChannelIcon (channelId) {
    return getChannelIcon(channelId)
  }

  getChannelColor (channelId) {
    return getChannelColor(channelId)
  }

  showModal = false
  headerImagePreview = null
  footerImagePreview = null

  campaignData = {
    name: '',
    status: 'DRAFT',
    status_nmDescricao: '',
    startDate: '',
    endDate: '',
    recurrenceType: null,
    recurrenceType_nmDescricao: '',
    leadTimeDays: null,
    customInterval: null,
    segmentationFilter: null,
    createdAt: '',
    template: null,
    campaignExecutions: []
  }

  recurrenceTypeOptions = []
  messageVariables = []

  @Watch('show')
  onShowChange (val) {
    this.showModal = val
  }

  mounted () {
    this.initializeRecurrenceTypeOptions()
  }

  onHide () {
    if (this.showModal) {
      this.showModal = false
      this.$emit('update:show', false)
    }
  }

  onClose () {
    this.onHide()
  }

  async onEdit () {
    this.$emit('edit', this.campaignData)
    await this.$nextTick()
    this.onHide()
  }

  async openDetail (id) {
    try {
      const [response, variablesForm] = await Promise.all([
        repository.resolve('campaign.detalhar', { id }),
        repository.resolveLogic('msgVariableEnum.obterTodos')
      ])
      this.messageVariables = this.normalizeVariables(variablesForm)

      // Extract dados form - response can be array or object
      const data = response?.[0]?.dadosForm || response?.dadosForm || response?.[0] || response || {}

      this.campaignData = this.mapCampaignData(data)
      // Load template images if available
      if (this.campaignData.template && this.campaignData.template.templateImages) {
        await this.loadExistingImages(this.campaignData.template.templateImages)
        this.campaignData.template.headerImage = this.headerImagePreview
        this.campaignData.template.footerImage = this.footerImagePreview
      }

      this.showModal = true
      this.$emit('update:show', true)
    } catch (error) {
      console.error('Erro ao carregar campanha:', error)
      messageBox.error(this.$gettext('ERROR_LOADING_FORM'))
      this.messageVariables = []
    }
  }

  normalizeVariables (data) {
    const records = Array.isArray(data)
      ? data
      : (Array.isArray(data?.registros) ? data.registros : (Array.isArray(data?.[0]?.registros) ? data[0].registros : []))

    return records
      .map(category => ({
        variableType: category.variableType,
        nmDescricao: category.nmDescricao,
        variables: Array.isArray(category.variables)
          ? category.variables.map(variable => ({
            technicalKey: variable.technicalKey,
            displayLabel: variable.displayLabel,
            displayTag: variable.displayTag,
            variableType: variable.variableType,
            sampleText: variable.sampleText
          }))
          : []
      }))
      .filter(category => category.variables.length > 0)
  }

  mapCampaignData (data) {
    // Verifica se existe um template (pode ser ID ou objeto com props template_)
    const hasTemplate = !!(data.template || (data.template_name && data.template_subject))
    const templateData = hasTemplate
      ? {
        id: data.template,
        name: data.template_name || '',
        available: data.template_available !== false,
        messagingPurpose: data.messagingPurpose,
        messagingPurpose_nmDescricao: data.messagingPurpose_nmDescricao || '',
        channelType: data.channelType,
        channelType_nmDescricao: data.channelType_nmDescricao || '',
        subject: data.template_subject || '',
        content: data.template_content || '',
        responsible_nmLogin: data.template_responsible_nmLogin || '',
        createdAt: data.template_createdAt || '',
        updatedAt: data.template_updatedAt || '',
        templateImages: data.template_templateImages || []
      }
      : null

    const executions = Array.isArray(data.campaignExecutions) ? data.campaignExecutions : []

    return {
      id: data.id,
      name: data.name || '',
      status: data.status || null,
      status_nmDescricao: data.status_nmDescricao || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      recurrenceType: data.recurrenceType || null,
      recurrenceType_nmDescricao: data.recurrenceType_nmDescricao || '',
      leadTimeDays: data.leadTimeDays || null,
      customInterval: data.customInterval || data.recurrenceCustomInterval || null,
      segmentationFilter: parseSegmentationFilter(data.segmentationFilter),
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || '',
      template: templateData,
      campaignExecutions: executions
    }
  }

  async loadExistingImages (images) {
    try {
      this.headerImagePreview = null
      this.footerImagePreview = null

      for (const img of images) {
        if (!img.nmArquivo || !img.templateImageType) {
          continue
        }

        const imageTypeId = img.templateImageType
        const isHeader = String(imageTypeId) === String(this.HEADER_IMAGE_TYPE)
        const isFooter = String(imageTypeId) === String(this.FOOTER_IMAGE_TYPE)

        if (isHeader || isFooter) {
          try {
            const blob = await this.$jt.repository.downloadFile(
              img.nmArquivo,
              null,
              'MESSAGING_TEMPLATE_IMAGES'
            )
            const blobUrl = URL.createObjectURL(blob)

            if (isHeader) {
              this.headerImagePreview = blobUrl
            } else if (isFooter) {
              this.footerImagePreview = blobUrl
            }
          } catch (error) {
            console.warn(`Erro ao baixar imagem ${img.nmArquivo}:`, error)
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar imagens existentes:', error)
    }
  }

  initializeRecurrenceTypeOptions () {
    const rt = recurrenceType()
    this.recurrenceTypeOptions = Object.values(rt).map(item => ({
      label: item.label,
      value: item.key
    }))
  }

  formatDate (dateStr) {
    if (!dateStr) return ''

    // Usa formatDateTimeLocal e extrai apenas a data (DD/MM/YYYY)
    const formatted = formatDateTimeLocal(dateStr)
    if (formatted) {
      return formatted.split(',')[0].split(' ')[0]
    }
    return dateStr
  }

  formatDateTime (dateStr) {
    if (!dateStr) return ''

    // Usa formatDateTimeLocal e remove os segundos (HH:mm apenas)
    const formatted = formatDateTimeLocal(dateStr)
    if (formatted) {
      const parts = String(formatted).split(',')
      const date = (parts[0] || '').trim()
      const timePart = (parts[1] || '').trim()

      if (!timePart) {
        return date || formatted
      }

      const [hours = '', minutes = ''] = timePart.split(':')
      return `${date} ${hours}:${minutes}`.trim()
    }
    return dateStr
  }

  formatAuditDateTime (dateStr) {
    if (!dateStr) return ''

    // Formata a data de auditoria sem conversao de timezone.
    const raw = String(dateStr).trim()
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?$/)

    if (match) {
      const [, year, month, day, hours, minutes] = match
      const formattedDate = `${day}/${month}/${year}`

      if (hours && minutes) {
        return `${formattedDate} ${hours}:${minutes}`
      }

      return formattedDate
    }

    return raw
  }

  formatAuditDate (dateStr) {
    if (!dateStr) return ''

    const formatted = this.formatAuditDateTime(dateStr)
    if (formatted) {
      return formatted.split(',')[0].split(' ')[0]
    }
    return dateStr
  }

  getCampaignStatusKey () {
    const status = this.campaignData.status
    const cs = campaignStatus()
    if (!status || !cs) return 'draft'

    for (const key in cs) {
      if (cs[key] && String(cs[key].key) === String(status)) {
        return key.toLowerCase()
      }
    }
    return 'draft'
  }

  getCampaignStatusIcon () {
    const statusKey = this.getCampaignStatusKey()
    const icons = {
      draft: 'edit',
      active: 'play_arrow',
      paused: 'pause',
      ended: 'check_circle'
    }
    return icons[statusKey] || 'edit'
  }

  getCampaignStatusLabel () {
    const cs = campaignStatus()
    const status = this.campaignData.status
    if (!status || !cs) return this.$gettext('DRAFT')

    for (const key in cs) {
      if (cs[key] && String(cs[key].key) === String(status)) {
        return cs[key].label
      }
    }
    return this.campaignData.status_nmDescricao || status
  }

  getPurposeIcon (purposeId) {
    if (!purposeId) return 'description'
    const p = messagingPurpose()
    if (!p) return 'description'
    const icons = {}
    if (p.ONE_TIME_PAYMENT) icons[String(p.ONE_TIME_PAYMENT.key)] = 'local_atm'
    if (p.INSTALLMENT) icons[String(p.INSTALLMENT.key)] = 'event'
    if (p.REGULAR_INVOICE) icons[String(p.REGULAR_INVOICE.key)] = 'notifications'
    if (p.NOTICE) icons[String(p.NOTICE.key)] = 'warning'
    if (p.ADDITIONAL) icons[String(p.ADDITIONAL.key)] = 'account_balance'
    return icons[String(purposeId)] || 'description'
  }

  getRecurrenceLabel () {
    if (this.campaignData.recurrenceType_nmDescricao) {
      return this.campaignData.recurrenceType_nmDescricao
    }

    if (!this.campaignData.recurrenceType) {
      return this.$gettext('SINGLE_SHIPMENT')
    }
    const option = this.recurrenceTypeOptions.find(opt => String(opt.value) === String(this.campaignData.recurrenceType))
    return option ? option.label : this.campaignData.recurrenceType
  }

  formatSegmentationFilterTitle (filter) {
    return formatSegmentationFilterTitle(filter)
  }

  getSegmentationFieldLabel (val) {
    if (val == null) return ''
    if (typeof val === 'string') return val
    if (typeof val === 'object') return val.label || val.name || JSON.stringify(val)
    return String(val)
  }

  toSnakeUpper (key) {
    if (!key && key !== 0) return ''
    try {
      const s = String(key)
        // insert underscore between lower->upper transitions
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        // replace spaces or hyphens with underscore
        .replace(/[\s-]+/g, '_')
      return s.toUpperCase()
    } catch (e) {
      return String(key).toUpperCase()
    }
  }
}

export default CampaignDetailModal
</script>

<style lang="stylus" scoped>
.modal-detail-campaign
  display: flex
  flex-direction: column
  width: 85vw
  max-width: 85vw

.detail-content
  overflow: hidden
  flex: 1
  padding: 16px !important

.detail-col-left
  height: calc(90vh - 70px)
  overflow-y: auto
  overflow-x: hidden
  padding-right: 8px
  padding-bottom: 88px
  box-sizing: border-box

.detail-col-right
  height: calc(90vh - 70px)
  overflow-y: auto
  overflow-x: hidden
  padding-bottom: 88px
  box-sizing: border-box
  &::v-deep .q-card,
  &::v-deep .q-card__section,
  &::v-deep .q-card-section
    overflow: visible !important

.card-template
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05)

.info-row
  display: flex
  flex-direction: column
  gap: 4px

.info-label
  font-size: 12px
  color: #707070
  text-transform: uppercase
  font-weight: 500

.info-value
  font-size: 14px
  color: #2c3f50

.status-chip
  &--draft
    background-color: rgba(107, 114, 126, 0.15)
    color: #6b727e

    ::v-deep .q-chip__icon
      color: #6b727e

  &--active
    background-color: rgba(0, 97, 59, 0.15)
    color: #00613b

    ::v-deep .q-chip__icon
      color: #00613b

  &--paused
    background-color: rgba(209, 142, 75, 0.15)
    color: #d18e4b

    ::v-deep .q-chip__icon
      color: #d18e4b

  &--ended
    background-color: rgba(0, 98, 169, 0.15)
    color: #0062a9

    ::v-deep .q-chip__icon
      color: #0062a9

  &--completed
    background-color: rgba(14, 136, 228, 0.15)
    color: #0e88e4

    ::v-deep .q-chip__icon
      color: #0e88e4

.status-badge
  display: inline-flex
  align-items: center
  gap: 6px
  padding: 6px 12px
  border-radius: 4px
  font-size: 12px

  &--draft
    background-color: rgba(107, 114, 126, 0.1)
    color: #6b727e

  &--active
    background-color: rgba(0, 97, 59, 0.1)
    color: #00613b

  &--paused
    background-color: rgba(209, 142, 75, 0.1)
    color: #d18e4b

  &--completed
    background-color: rgba(14, 136, 228, 0.1)
    color: #0e88e4

.status-dot
  display: inline-block
  width: 8px
  height: 8px
  border-radius: 50%

  &--active
    background-color: #00613b

  &--inactive
    background-color: #d32f2f

.campaign-status-dot
  width: 12px
  height: 12px
  border-radius: 50%
  display: inline-block
.template-card
  display: flex
  align-items: center
  gap: 12px
  padding: 12px
  border-radius: 8px
  border: 1px solid #e0e0e0

.template-card-icon
  flex-shrink: 0
  display: flex
  align-items: center
  justify-content: center

.icon-stack
  position: relative
  width: 48px
  height: 48px
  display: flex
  align-items: center
  justify-content: center

.purpose-icon-wrapper
  width: 48px
  height: 48px
  border-radius: 8px
  background-color: rgba(14, 136, 228, 0.08)
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  color: #0e88e4

.purpose-icon
  position: absolute
  z-index: 1

.channel-icon
  position: absolute
  bottom: 0
  right: 0
  z-index: 2
  background-color: white
  border-radius: 50%
  padding: 2px

.template-card-content
  flex: 1

.template-card-name
  font-size: 14px
  font-weight: 500
  color: #2c3f50
  margin: 0

.template-card-meta
  font-size: 12px
  color: #707070
  margin: 4px 0 0 0

.template-card-status
  display: flex
  align-items: center
  gap: 6px
  flex-shrink: 0

.metrics-row
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 12px

.metric-card
  padding: 12px
  border-radius: 8px
  text-align: center

  &--sent
    background-color: rgba(0, 97, 59, 0.05)
    border-color: rgba(0, 97, 59, 0.2)

  &--delivered
    background-color: rgba(14, 136, 228, 0.05)
    border-color: rgba(14, 136, 228, 0.2)

  &--failed
    background-color: rgba(211, 47, 47, 0.05)
    border-color: rgba(211, 47, 47, 0.2)

.metric-label
  font-size: 12px
  color: #707070
  text-transform: uppercase
  font-weight: 500
  margin: 0 0 8px 0

.metric-value
  font-size: 24px
  font-weight: 700
  color: #2c3f50

.empty-state
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 32px 16px
  text-align: center

  p
    margin: 8px 0 0 0

.detail-footer
  background-color: #ffffff
  border-top: 1px solid rgba(0, 0, 0, 0.12)

</style>
